import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Label, Row, Col, Progress } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";

// Carga masiva de gastos desde un Excel (layout). Cada renglón hace lo mismo que
// "Guardar" en GastosGeneralCreate: crea el gasto (/gastosGeneral) y su egreso en
// el banco (/movimientosBancos, importe negativo, moneda del banco).
//
// Las columnas que vienen de otro catálogo son listas desplegables, solo
// seleccionables. Categoría y subcategoría van juntas ("Categoria › Sub") porque
// la subcategoría depende de la categoría. EMPRESA vacía = la única activa.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const env = (k) => process.env[k];
const txt = (v) => String(v === undefined || v === null ? "" : v).trim();
const nu = (v) => Number(String(v ?? "").replace(/[$,\s]/g, "")) || 0;
const norm = (v) => txt(v).toUpperCase().replace(/\s+/g, " ");
const r2 = (n) => Math.round(n * 100) / 100;
const SEP_CAT = " › ";
const FILAS_LAYOUT = 500;
const IMPUESTOS = [["No", 0], ["Si 16%", 16]];

const COLUMNAS = [
  ["FECHA", true],
  ["EMPRESA", false],
  ["BANCO", true],
  ["CATEGORIA / SUBCATEGORIA", true],
  ["SOLICITO / AUTORIZO", true],
  ["DESCRIPCION", false],
  ["IMPUESTOS", false],
  ["SUBTOTAL", true],
];

const etiquetaBanco = (b) => `${txt(`${txt(b.banco)} ${txt(b.cuenta)}`).replace(/\s+/g, " ")} (${txt(b.moneda)})`;
const etiquetaSub = (s) => `${txt(s.categorias?.[0]?.name)}${SEP_CAT}${txt(s.name)}`;

// Fecha del Excel -> "YYYY-MM-DD" (serial de Excel, Date o texto AAAA-MM-DD / DD/MM/AAAA)
function aFecha(v) {
  const dos = (n) => String(n).padStart(2, "0");
  if (typeof v === "number" && v > 0) {
    const d = XLSX.SSF.parse_date_code(v);
    return d ? `${d.y}-${dos(d.m)}-${dos(d.d)}` : "";
  }
  if (v instanceof Date && !isNaN(v)) return `${v.getFullYear()}-${dos(v.getMonth() + 1)}-${dos(v.getDate())}`;
  const t = txt(v);
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return `${m[1]}-${dos(m[2])}-${dos(m[3])}`;
  m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${dos(m[2])}-${dos(m[1])}`;
  return "";
}

function ImportarGastosModal({ abierto, cerrar, onTerminado, userId }) {
  const [cats, setCats] = useState({});
  const [filas, setFilas] = useState([]);
  const [archivo, setArchivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [avance, setAvance] = useState(0);

  useEffect(() => {
    if (!abierto) return;
    setFilas([]); setArchivo(""); setAvance(0); setCats({});
    const pide = (url) => axios.get(url, { headers: headers() }).then((r) => r.data || []).catch(() => []);
    Promise.all([
      pide(env("REACT_APP_URL_EMPRESAS")),
      pide(env("REACT_APP_URL_BANCOS")),
      pide(env("REACT_APP_URL_SUB_CATEGORIAS")),
      pide(env("REACT_APP_URL_SOLICITANTES")),
    ]).then(([empresas, bancos, subCategorias, solicitantes]) =>
      setCats({ empresas, bancos, subCategorias, solicitantes })
    );
  }, [abierto]);

  const activos = (lista) => (lista || []).filter((x) => x.is_active !== "No");
  // Subcategorías con categoría (las que la pantalla permite guardar)
  const subsValidas = () => activos(cats.subCategorias).filter((s) => s.categorias?.[0]?._id);

  const listas = () => ({
    EMPRESA: activos(cats.empresas).map((e) => txt(e.name)),
    BANCO: activos(cats.bancos).map(etiquetaBanco),
    "CATEGORIA / SUBCATEGORIA": subsValidas().map(etiquetaSub),
    "SOLICITO / AUTORIZO": activos(cats.solicitantes).map((s) => txt(s.name)),
    IMPUESTOS: IMPUESTOS.map(([e]) => e),
  });

  async function descargaLayout() {
    const wb = new ExcelJS.Workbook();
    const hoja = wb.addWorksheet("Gastos"); // 1a hoja: es la que se lee al subir
    const hojaCat = wb.addWorksheet("Catalogos", { state: "hidden" });
    const hojaInst = wb.addWorksheet("Instrucciones");

    hoja.columns = COLUMNAS.map(([c, ob]) => ({
      header: ob ? `${c} *` : c,
      key: c,
      width: c === "CATEGORIA / SUBCATEGORIA" || c === "BANCO" ? 38 : Math.max(14, c.length + 4),
    }));
    hoja.getRow(1).font = { bold: true };
    hoja.views = [{ state: "frozen", ySplit: 1 }];
    hoja.getColumn(1).numFmt = "yyyy-mm-dd";

    const valores = listas();
    Object.entries(valores).forEach(([col, lista], i) => {
      const letra = String.fromCharCode(65 + i);
      hojaCat.getCell(1, i + 1).value = col;
      lista.sort((a, b) => a.localeCompare(b)).forEach((v, j) => (hojaCat.getCell(j + 2, i + 1).value = v));
      if (!lista.length) return;
      const colLayout = COLUMNAS.findIndex(([c]) => c === col) + 1;
      const rango = `Catalogos!$${letra}$2:$${letra}$${lista.length + 1}`;
      for (let r = 2; r <= FILAS_LAYOUT + 1; r++) {
        hoja.getCell(r, colLayout).dataValidation = {
          type: "list", allowBlank: true, formulae: [rango],
          showErrorMessage: true, errorTitle: `${col} no válido`, error: `Elige un valor de la lista de ${col}.`,
        };
      }
    });
    for (let r = 2; r <= FILAS_LAYOUT + 1; r++) {
      hoja.getCell(r, 1).dataValidation = {
        type: "date", operator: "greaterThan", allowBlank: true,
        formulae: [new Date(2000, 0, 1)], showErrorMessage: true, error: "Escribe una fecha válida (AAAA-MM-DD).",
      };
      hoja.getCell(r, COLUMNAS.length).dataValidation = {
        type: "decimal", operator: "greaterThan", allowBlank: true,
        formulae: [0], showErrorMessage: true, error: "El SUBTOTAL debe ser un número mayor a 0.",
      };
    }

    const empresasAct = activos(cats.empresas);
    [
      ["COMO LLENAR ESTE LAYOUT"],
      [],
      ["1", "Captura un gasto por renglón, desde el renglón 2 de la hoja Gastos."],
      ["2", "Obligatorias (*): FECHA, BANCO, CATEGORIA / SUBCATEGORIA, SOLICITO / AUTORIZO y SUBTOTAL."],
      ["3", "EMPRESA, BANCO, CATEGORIA / SUBCATEGORIA, SOLICITO / AUTORIZO e IMPUESTOS se eligen de la lista desplegable (vienen del sistema)."],
      ["4", "Cada gasto se registra como egreso del BANCO elegido, en la moneda de ese banco (igual que en la pantalla)."],
      ["5", "IMPUESTOS vacío = No. Con 'Si 16%' el IVA se calcula sobre el SUBTOTAL."],
      ["6", empresasAct.length === 1 ? `EMPRESA vacía = ${empresasAct[0].name}.` : "EMPRESA es obligatoria."],
      ["7", "FECHA en formato AAAA-MM-DD (o como fecha de Excel). DESCRIPCION vacía = NA."],
    ].forEach((r) => hojaInst.addRow(r));
    hojaInst.getColumn(1).width = 6;
    hojaInst.getColumn(2).width = 120;
    hojaInst.getRow(1).font = { bold: true };

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "LayoutGastos.xlsx";
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function leeArchivo(file) {
    if (!file) return;
    setArchivo(file.name); setAvance(0);
    const lector = new FileReader();
    lector.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const crudas = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "", raw: true });
        setFilas(revisa(crudas));
      } catch (err) {
        Swal.fire({ icon: "error", title: "No se pudo leer el archivo", text: "Usa el layout que se descarga de este mismo botón." });
      }
    };
    lector.readAsArrayBuffer(file);
  }

  function revisa(crudas) {
    const dameRaw = (f, col) => {
      const k = Object.keys(f).find((x) => norm(x).replace(/\*/g, "").trim() === col);
      return k ? f[k] : "";
    };
    const dame = (f, col) => txt(dameRaw(f, col));
    const empresasAct = activos(cats.empresas);
    const bancos = activos(cats.bancos);
    const subs = subsValidas();
    const sols = activos(cats.solicitantes);

    return crudas.map((f, i) => {
      const fila = { renglon: i + 2 };
      const vacio = COLUMNAS.every(([c]) => !dame(f, c));
      if (vacio) return { ...fila, estado: "vacio" };

      const fecha = aFecha(dameRaw(f, "FECHA"));
      const bancoTxt = dame(f, "BANCO");
      const catTxt = dame(f, "CATEGORIA / SUBCATEGORIA");
      const solTxt = dame(f, "SOLICITO / AUTORIZO");
      const subtotal = r2(nu(dame(f, "SUBTOTAL")));
      Object.assign(fila, { fecha, banco: bancoTxt, categoria: catTxt, subtotal });

      const errores = [];
      if (!fecha) errores.push("FECHA inválida");
      if (!(subtotal > 0)) errores.push("SUBTOTAL debe ser mayor a 0");
      const banco = bancos.find((b) => norm(etiquetaBanco(b)) === norm(bancoTxt));
      if (!banco) errores.push(bancoTxt ? `BANCO "${bancoTxt}" no existe` : "Falta BANCO");
      const sub = subs.find((s) => norm(etiquetaSub(s)) === norm(catTxt));
      if (!sub) errores.push(catTxt ? `CATEGORIA "${catTxt}" no existe` : "Falta CATEGORIA / SUBCATEGORIA");
      const sol = sols.find((s) => norm(s.name) === norm(solTxt));
      if (!sol) errores.push(solTxt ? `SOLICITO "${solTxt}" no existe` : "Falta SOLICITO / AUTORIZO");
      let empTxt = dame(f, "EMPRESA");
      if (!empTxt && empresasAct.length === 1) empTxt = empresasAct[0].name;
      const empresa = empresasAct.find((x) => norm(x.name) === norm(empTxt));
      if (!empresa) errores.push(empTxt ? `EMPRESA "${empTxt}" no existe` : "Falta EMPRESA");
      const impTxt = dame(f, "IMPUESTOS") || "No";
      const imp = IMPUESTOS.find(([e]) => norm(e) === norm(impTxt));
      if (!imp) errores.push(`IMPUESTOS "${impTxt}" no válido`);
      if (errores.length) return { ...fila, estado: "error", detalle: errores.join(", ") };

      const iva = r2(subtotal * (imp[1] / 100));
      const total = r2(subtotal + iva);
      return {
        ...fila,
        estado: "nuevo",
        total,
        moneda: banco.moneda,
        gasto: {
          is_active: "Si",
          fecha,
          subtotal,
          iva,
          total,
          descripcion: dame(f, "DESCRIPCION") || "NA",
          areas: env("REACT_APP_AREAS_GENERAL"),
          categorias: sub.categorias[0]._id,
          subCategorias: sub._id,
          solicitantes: sol._id,
          bancos: banco._id,
          empresas: empresa._id,
        },
      };
    }).filter((f) => f.estado !== "vacio");
  }

  const nuevos = filas.filter((f) => f.estado === "nuevo");
  const errores = filas.filter((f) => f.estado === "error");
  const listo = !!cats.bancos;
  const totalPorMoneda = nuevos.reduce((acc, f) => ({ ...acc, [f.moneda]: r2((acc[f.moneda] || 0) + f.total) }), {});
  const fmt = (n) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  async function guardar() {
    if (!nuevos.length) return;
    const resumen = Object.entries(totalPorMoneda).map(([m, t]) => `$${fmt(t)} ${m}`).join(" + ");
    const ok = await Swal.fire({
      icon: "question",
      title: `Se registrarán ${nuevos.length} gasto(s)`,
      html: `Total: <b>${resumen}</b> (egresos de banco)` +
        (errores.length ? `<br><b style="color:#b45309">${errores.length} renglón(es) con error se omiten.</b>` : ""),
      showCancelButton: true,
      confirmButtonText: "Sí, registrar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed && !ok.value) return;
    setGuardando(true);
    const fallaron = [];
    for (let i = 0; i < nuevos.length; i++) {
      const f = nuevos[i];
      try {
        const r = await axios.post(env("REACT_APP_URL_GASTOS_GENERAL"), f.gasto, { headers: headers() });
        await axios.post(
          env("REACT_APP_URL_MOVIMIENTOS_BANCOS"),
          {
            fecha: f.gasto.fecha,
            importe: -f.total,
            tipo: "Egreso",
            bancos: f.gasto.bancos,
            gastosGeneral: r.data._id,
            moneda: f.moneda,
            tipoCambio: 1,
          },
          { headers: headers() }
        );
      } catch (error) {
        fallaron.push(`Renglón ${f.renglon}: ${error.response?.data?.message || error.message}`);
      }
      setAvance(Math.round(((i + 1) / nuevos.length) * 100));
    }
    try {
      await axios.post(
        env("REACT_APP_URL_LOGS"),
        { tipo: "Carga masiva gastos", detalle: `${nuevos.length - fallaron.length} gastos desde layout`, user: userId },
        { headers: headers() }
      );
    } catch (e) { /* el log no detiene la carga */ }
    setGuardando(false);
    await Swal.fire({
      icon: fallaron.length ? "warning" : "success",
      title: `${nuevos.length - fallaron.length} gasto(s) registrados`,
      html: fallaron.length ? `No se pudieron registrar:<br><small>${fallaron.slice(0, 10).join("<br>")}</small>` : "",
    });
    cerrar();
    if (onTerminado) onTerminado();
  }

  const color = { nuevo: "#15803d", error: "#dc2626" };
  return (
    <Modal size="lg" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>Carga masiva de gastos (layout)</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={5}>
            <Label><b>1. Layout</b></Label>
            <div>
              <Button size="sm" color="info" onClick={descargaLayout} disabled={!listo}>
                <i className="far fa-file-excel" /> Descargar layout
              </Button>
            </div>
            <small style={{ color: "#64748b" }}>
              {listo ? "Empresa, banco, categoría, solicitante e impuestos vienen como listas del sistema." : "Cargando catálogos..."}
            </small>
          </Col>
          <Col md={7}>
            <Label><b>2. Sube el layout lleno</b></Label>
            <Input type="file" accept=".xlsx,.xls" disabled={!listo} onChange={(e) => leeArchivo(e.target.files[0])} />
            {archivo ? <small style={{ color: "#64748b" }}>{archivo}</small> : null}
          </Col>
        </Row>
        <br />
        {filas.length ? (
          <>
            <div style={{ display: "flex", gap: 14, marginBottom: 6, fontSize: 13 }}>
              <span style={{ color: color.nuevo, fontWeight: 700 }}>{nuevos.length} por registrar</span>
              <span style={{ color: color.error, fontWeight: 700 }}>{errores.length} con error</span>
              {Object.entries(totalPorMoneda).map(([m, t]) => (
                <span key={m} style={{ fontWeight: 700 }}>Total {m}: ${fmt(t)}</span>
              ))}
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <Table size="sm" bordered>
                <thead>
                  <tr><th>Renglón</th><th>Fecha</th><th>Categoría</th><th>Banco</th><th>Total</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f.renglon}>
                      <td>{f.renglon}</td>
                      <td>{f.fecha}</td>
                      <td>{f.categoria}</td>
                      <td>{f.banco}</td>
                      <td>{f.total !== undefined ? `$${fmt(f.total)}` : ""}</td>
                      <td style={{ color: color[f.estado], fontWeight: 600 }}>
                        {f.estado === "nuevo" ? "Se registrará" : f.detalle}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <p style={{ color: "#9ca3af" }}>Sube el layout para ver qué se va a registrar.</p>
        )}
        {guardando ? <Progress animated value={avance}>{avance}%</Progress> : null}
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={guardar} disabled={guardando || !nuevos.length}>
          {guardando ? "Registrando..." : `Registrar ${nuevos.length} gasto(s)`}
        </Button>
        <Button color="secondary" onClick={cerrar} disabled={guardando}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default ImportarGastosModal;
